import {
  wechatLoginSettingsSchema,
  upsertWechatLoginSettingsSchema,
  adminPaymentOrderDetailSchema,
  adminPaymentOrderListQuerySchema,
  adminPaymentOrderListResultSchema,
  apiErrorSchema,
  apiSuccessSchema,
  appConfigSchema,
  appListQuerySchema,
  appSchema,
  createAppInputSchema,
  createAppResultSchema,
  credentialMetadataSchema,
  credentialRevealSchema,
  createPaymentRefundInputSchema,
  payChannelConfigSchema,
  paymentConfigSchema,
  paymentRefundSchema,
  paymentReconciliationBatchSchema,
  paymentReconciliationDifferenceSchema,
  paymentReconciliationQuerySchema,
  paymentStatementBatchSchema,
  paymentStatementJobSchema,
  paymentStatementImportInputSchema,
  paymentStatementWorkspaceSchema,
  paymentWebhookEventDetailSchema,
  paymentWebhookEventListQuerySchema,
  paymentWebhookEventListResultSchema,
  paymentWebhookEventSchema,
  triggerPaymentStatementJobInputSchema,
  updatePaymentDifferenceInputSchema,
  upsertPaymentConfigInputSchema,
  updateAppInputSchema,
  upsertPayChannelInputSchema,
  type AppConfig,
  type AppRecord,
  type AdminPaymentOrder,
  type PayChannel,
  type PayChannelConfig,
} from "@yahshi/contracts";
import { z } from "zod";
import type {
  CloudBaseRdbClient,
  CloudBaseRdbError,
  CloudBaseWebClient,
} from "../../../lib/cloudBaseWebClient";
import type { AppListFilters, ConfigUpsertInput, M1AppApi } from "./M1AppApi";
import { M1ApiError } from "./M1AppApi";

type CloudBaseM1AppApiOptions = {
  client: CloudBaseWebClient;
  postgresInstance: string;
  functionName?: string;
  onUnauthorized?: (error: M1ApiError) => void | Promise<void>;
};

const pgIntegerSchema = z.union([
  z.number().int().nonnegative(),
  z.string().regex(/^\d+$/).transform(Number),
]);

const pgTimestampSchema = z.string().transform((value, context) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    context.addIssue({ code: "custom", message: "Invalid PostgreSQL timestamp" });
    return z.NEVER;
  }
  return date.toISOString();
});

const rawAppSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  description: z.string(),
  status: z.string(),
  platforms: z.array(z.string()),
  pay_channels: z.array(z.string()),
  config_count: pgIntegerSchema,
  updated_at: pgTimestampSchema,
  total_count: pgIntegerSchema.optional(),
  active_count: pgIntegerSchema.optional(),
});

const rawConfigSchema = z.object({
  key: z.string(),
  value: z.string(),
  value_type: z.string(),
  is_public: z.boolean(),
  description: z.string(),
});

const rawPayChannelSchema = z.object({
  channel: z.string(),
  enabled: z.boolean(),
  account_id: z.string(),
  secret_ref: z.string(),
  description: z.string(),
  updated_at: pgTimestampSchema,
});

const rawPaymentConfigSchema = z.object({
  id: z.string(),
  name: z.string(),
  channel: z.enum(["wxpay", "alipay", "apple"]),
  scenarios: z.array(z.string()),
  account_id: z.string(),
  secret_ref: z.string(),
  credential_refs: z.unknown().optional(),
  protocol_config: z.unknown().optional(),
  notify_url: z.string().optional().nullable(),
  description: z.string(),
  enabled: z.boolean(),
  updated_at: pgTimestampSchema,
  associated_apps: z.unknown().optional(),
  credentials_encrypted: z.boolean().optional().default(false),
});

function normalizeRows(value: unknown): unknown[] {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

function toApp(value: unknown): AppRecord {
  const row = rawAppSchema.parse(value);
  return appSchema.parse({
    id: row.id,
    name: row.name,
    code: row.code,
    description: row.description,
    status: row.status,
    platforms: row.platforms,
    payChannels: row.pay_channels,
    configCount: row.config_count,
    updatedAt: row.updated_at,
  });
}

function toConfig(value: unknown): AppConfig {
  const row = rawConfigSchema.parse(value);
  return appConfigSchema.parse({
    key: row.key,
    value: row.value,
    valueType: row.value_type,
    isPublic: row.is_public,
    description: row.description,
  });
}

function toPayChannel(value: unknown): PayChannelConfig {
  const row = rawPayChannelSchema.parse(value);
  return payChannelConfigSchema.parse({
    channel: row.channel,
    enabled: row.enabled,
    accountId: row.account_id,
    secretRef: row.secret_ref,
    description: row.description,
    updatedAt: row.updated_at,
  });
}

function toPaymentConfig(value: unknown) {
  const row = rawPaymentConfigSchema.parse(value);
  const credentialRefs = z.record(z.string(), z.string()).catch({}).parse(row.credential_refs ?? {});
  return paymentConfigSchema.parse({
    id: row.id,
    name: row.name,
    channel: row.channel,
    scenarios: row.scenarios,
    accountId: row.account_id,
    secretRef: row.secret_ref,
    credentialRefs,
    credentialStatus: row.credentials_encrypted ? Object.fromEntries(Object.keys(credentialRefs).map((key) => [key, true])) : {},
    protocolConfig: row.protocol_config ?? {},
    notifyUrl: row.notify_url ?? undefined,
    description: row.description,
    enabled: row.enabled,
    updatedAt: row.updated_at,
    associatedApps: row.associated_apps ?? [],
  });
}

function rpcError(error: CloudBaseRdbError): M1ApiError {
  const code = error.code ?? "";
  const message = error.message ?? "";
  if (code === "28000" || message.includes("M1_UNAUTHENTICATED")) {
    return new M1ApiError("UNAUTHORIZED", "登录状态已失效，请重新登录");
  }
  if (code === "42501" || message.includes("M1_ADMIN_FORBIDDEN")) {
    return new M1ApiError("ADMIN_FORBIDDEN", "当前账号没有管理员权限");
  }
  if (message.includes("M1_APP_VERSION_CONFLICT")) return new M1ApiError("APP_VERSION_CONFLICT", "应用已更新，请加载最新信息后重新编辑");
  if (message.includes("M1_APP_IN_USE")) return new M1ApiError("APP_IN_USE", "该应用已有用户或订单，不能删除，请先停用");
  if (code === "23505") {
    return new M1ApiError("APP_CODE_CONFLICT", "应用标识已存在", { code: "应用标识已存在" });
  }
  if (message.includes("M1_APP_NOT_FOUND")) return new M1ApiError("APP_NOT_FOUND", "应用不存在");
  if (message.includes("M1_CONFIG_NOT_FOUND")) return new M1ApiError("CONFIG_NOT_FOUND", "配置不存在");
  if (message.includes("M1_PAY_CHANNEL_NOT_FOUND")) return new M1ApiError("PAY_CHANNEL_NOT_FOUND", "支付渠道不存在");
  if (message.includes("M3_PAYMENT_CONFIG_NOT_FOUND")) return new M1ApiError("PAYMENT_CONFIG_NOT_FOUND", "支付配置不存在");
  if (message.includes("M3_PAYMENT_CONFIG_IN_USE")) return new M1ApiError("PAYMENT_CONFIG_IN_USE", "支付配置仍有关联应用，不能删除");
  if (message.includes("M3_PAYMENT_CONFIG_CHANNEL_CONFLICT")) return new M1ApiError("PAYMENT_CONFIG_CHANNEL_CONFLICT", "同一应用不能关联多个相同支付渠道配置");
  if (message.includes("M3_PAYMENT_CONFIG_LINK_NOT_FOUND")) return new M1ApiError("PAYMENT_CONFIG_LINK_NOT_FOUND", "支付配置关联不存在");
  if (message.includes("M3_STATEMENT_BATCH_NOT_FOUND")) return new M1ApiError("PAYMENT_STATEMENT_NOT_FOUND", "渠道账单批次不存在");
  if (message.includes("M3_STATEMENT_IMPORT_DUPLICATE_ORDER")) return new M1ApiError("PAYMENT_STATEMENT_DUPLICATE_ORDER", "账单中存在重复商户订单号");
  if (message.includes("M3_STATEMENT_IMPORT_INVALID") || message.includes("M3_STATEMENT_BATCH_INVALID")) return new M1ApiError("VALIDATION_ERROR", "请检查渠道账单内容");
  if (message.includes("M1_INVALID_CONFIG_VALUE")) {
    return new M1ApiError("INVALID_CONFIG_VALUE", "配置值与声明类型不匹配", { value: "配置值与声明类型不匹配" });
  }
  if (code === "22023" || message.startsWith("M1_INVALID_") || message === "M1_EMPTY_UPDATE") {
    return new M1ApiError("VALIDATION_ERROR", "请检查提交内容");
  }
  return new M1ApiError("CLOUDBASE_RDB_ERROR", "数据服务暂时不可用，请稍后重试");
}

function booleanResult(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.length === 1 && booleanResult(value[0]);
  if (value && typeof value === "object") {
    const values = Object.values(value as Record<string, unknown>);
    return values.length === 1 && values[0] === true;
  }
  return false;
}

export function createCloudBaseM1AppApi(options: CloudBaseM1AppApiOptions): M1AppApi {
  const db: CloudBaseRdbClient = options.client.rdb({
    instance: options.postgresInstance,
    schema: "public",
  });
  const functionName = options.functionName ?? "m1-app-admin";

  async function notifyUnauthorized(error: M1ApiError): Promise<void> {
    if (error.code === "UNAUTHORIZED" || error.code === "ADMIN_FORBIDDEN") {
      await options.onUnauthorized?.(error);
    }
  }

  async function rpc(name: string, parameters: Record<string, unknown>): Promise<unknown> {
    const response = await db.rpc(name, parameters);
    if (response.error) {
      const error = rpcError(response.error);
      await notifyUnauthorized(error);
      throw error;
    }
    return response.data;
  }

  async function callable<T>(
    action: "createApp" | "resetCredential" | "createPaymentRefund" | "upsertPaymentConfig" | "upsertWechatLoginSettings",
    data: Record<string, unknown>,
    schema: z.ZodType<T>,
  ): Promise<T> {
    let result: unknown;
    try {
      const response = await options.client.callFunction<unknown>({
        name: functionName,
        data: { action, ...data },
        parse: true,
      });
      result = response.result;
    } catch {
      throw new M1ApiError("CLOUDBASE_FUNCTION_ERROR", "安全操作暂时不可用，请稍后重试");
    }

    const failure = apiErrorSchema.safeParse(result);
    if (failure.success) {
      const error = new M1ApiError(
        failure.data.error.code,
        failure.data.error.message,
        failure.data.error.fields,
      );
      await notifyUnauthorized(error);
      throw error;
    }
    const success = apiSuccessSchema(schema).safeParse(result);
    if (!success.success) throw new M1ApiError("INVALID_RESPONSE", "服务响应格式无效");
    return success.data.data;
  }

  return {
    async listApps(filters: AppListFilters) {
      const query = appListQuerySchema.parse(filters);
      const readPage = async (page: number) => normalizeRows(await rpc("admin_list_apps", {
        p_keyword: query.keyword,
        p_status: query.status,
        p_page: page,
        p_page_size: query.pageSize,
      })).map((value) => rawAppSchema.parse(value));
      const parsedRows = await readPage(query.page);
      // The rowset RPC carries totals on each row; an empty later page loses
      // those totals. Read page one for metadata, without returning its items
      // as though they belonged to the requested page.
      const summary = parsedRows[0] ?? (query.page > 1 ? (await readPage(1))[0] : undefined);
      return {
        items: parsedRows.map(toApp),
        total: summary?.total_count ?? 0,
        activeTotal: summary?.active_count ?? 0,
      };
    },
    async getApp(appId) {
      const row = normalizeRows(await rpc("admin_get_app", { p_app_id: appId }))[0];
      if (!row) throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
      return toApp(row);
    },
    async createApp(input) {
      const parsed = createAppInputSchema.parse(input);
      return callable("createApp", { input: parsed }, createAppResultSchema);
    },
    async updateApp(appId, input) {
      const parsed = updateAppInputSchema.parse(input);
      const row = normalizeRows(await rpc("admin_update_app_checked", {
        p_expected_updated_at: parsed.expectedUpdatedAt,
        p_app_id: appId,
        p_name: parsed.name ?? null,
        p_code: parsed.code ?? null,
        p_description: parsed.description ?? null,
        p_status: parsed.status ?? null,
        p_platforms: parsed.platforms ?? null,
      }))[0];
      if (!row) throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
      return toApp(row);
    },
    async deleteApp(appId) {
      const result = await rpc("admin_delete_app", { p_app_id: appId });
      if (!booleanResult(result)) throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
    },
    async getCredential(appId) {
      const row = normalizeRows(await rpc("admin_get_credential_metadata", { p_app_id: appId }))[0];
      if (!row) throw new M1ApiError("CREDENTIAL_NOT_FOUND", "未找到应用凭证");
      const parsed = z.object({ app_key: z.string(), secret_last_reset_at: pgTimestampSchema }).parse(row);
      return credentialMetadataSchema.parse({
        appKey: parsed.app_key,
        secretLastResetAt: parsed.secret_last_reset_at,
      });
    },
    resetCredential(appId) {
      return callable("resetCredential", { appId }, credentialRevealSchema);
    },
    async getWechatLoginSettings(appId) {
      return wechatLoginSettingsSchema.parse(await rpc("admin_get_wechat_login_settings", { p_app_id: appId }));
    },
    upsertWechatLoginSettings(appId, input) {
      return callable("upsertWechatLoginSettings", { appId, input: upsertWechatLoginSettingsSchema.parse(input) }, wechatLoginSettingsSchema);
    },
    async listConfigs(appId) {
      return normalizeRows(await rpc("admin_list_configs", { p_app_id: appId })).map(toConfig);
    },
    async upsertConfig(appId, input: ConfigUpsertInput) {
      const parsed = appConfigSchema.parse(input);
      const row = normalizeRows(await rpc("admin_upsert_config", {
        p_app_id: appId,
        p_key: parsed.key,
        p_value: parsed.value,
        p_value_type: parsed.valueType,
        p_is_public: parsed.isPublic,
        p_description: parsed.description,
      }))[0];
      if (!row) throw new M1ApiError("INVALID_RESPONSE", "服务响应格式无效");
      return toConfig(row);
    },
    async deleteConfig(appId, key) {
      const result = await rpc("admin_delete_config", { p_app_id: appId, p_key: key });
      if (!booleanResult(result)) throw new M1ApiError("INVALID_RESPONSE", "服务响应格式无效");
    },
    async listPayChannels(appId) {
      return normalizeRows(await rpc("admin_list_pay_channels", { p_app_id: appId })).map(toPayChannel);
    },
    async upsertPayChannel(appId, input) {
      const parsed = upsertPayChannelInputSchema.parse(input);
      const row = normalizeRows(await rpc("admin_upsert_pay_channel", {
        p_app_id: appId,
        p_channel: parsed.channel,
        p_enabled: parsed.enabled,
        p_account_id: parsed.accountId,
        p_secret_ref: parsed.secretRef,
        p_description: parsed.description,
      }))[0];
      if (!row) throw new M1ApiError("INVALID_RESPONSE", "服务响应格式无效");
      return toPayChannel(row);
    },
    async deletePayChannel(appId, channel: PayChannel) {
      const result = await rpc("admin_delete_pay_channel", {
        p_app_id: appId,
        p_channel: channel,
      });
      if (!booleanResult(result)) throw new M1ApiError("INVALID_RESPONSE", "服务响应格式无效");
    },
    async listPaymentOrders(input) {
      const query = adminPaymentOrderListQuerySchema.parse(input);
      const result = await rpc("admin_list_payment_orders", {
        p_keyword: query.keyword,
        p_status: query.status,
        p_app_id: query.appId || null,
        p_channel: query.channel,
        p_page: query.page,
        p_page_size: query.pageSize,
      });
      return adminPaymentOrderListResultSchema.parse(result);
    },
    async getPaymentOrder(orderId) {
      const result = await rpc("admin_get_payment_order", { p_order_id: orderId });
      if (!result) throw new M1ApiError("PAYMENT_ORDER_NOT_FOUND", "支付订单不存在");
      return adminPaymentOrderDetailSchema.parse(result);
    },
    async syncPaymentOrder() {
      throw new M1ApiError("PAYMENT_SYNC_UNAVAILABLE", "主动查单需要启用 HTTP 管理服务模式");
    },
    async syncPaymentRefund() {
      throw new M1ApiError("PAYMENT_SYNC_UNAVAILABLE", "主动查退款需要启用 HTTP 管理服务模式");
    },
    async getPaymentReconciliation(input) {
      const query = paymentReconciliationQuerySchema.parse(input);
      try {
        const result = await rpc("admin_payment_reconciliation", { p_date_from: query.dateFrom, p_date_to: query.dateTo, p_channel: query.channel });
        return paymentReconciliationBatchSchema.parse(result);
      } catch (error) {
        if (!(error instanceof M1ApiError) || error.code === "UNAUTHORIZED" || error.code === "ADMIN_FORBIDDEN") throw error;
        const orders: AdminPaymentOrder[] = [];
        for (let page = 1; page <= 100; page += 1) {
          const result = adminPaymentOrderListResultSchema.parse(await rpc("admin_list_payment_orders", { p_keyword: "", p_status: "all", p_app_id: null, p_channel: query.channel, p_page: page, p_page_size: 100 }));
          orders.push(...result.items.filter((order) => order.createdAt.slice(0, 10) >= query.dateFrom && order.createdAt.slice(0, 10) <= query.dateTo && (order.status === "succeeded" || order.status === "refunded")));
          if (result.items.length < 100 || result.items.at(-1)!.createdAt.slice(0, 10) < query.dateFrom) break;
        }
        const details = await Promise.all(orders.map((order) => rpc("admin_get_payment_order", { p_order_id: order.id }).then((value) => adminPaymentOrderDetailSchema.parse(value))));
        const rowsMap = new Map<string, { channel: "wxpay" | "alipay" | "apple"; orderCount: number; grossAmountMinor: number; refundedAmountMinor: number; netAmountMinor: number; mismatchCount: number }>();
        orders.forEach((order, index) => {
          const row = rowsMap.get(order.channel) ?? { channel: order.channel, orderCount: 0, grossAmountMinor: 0, refundedAmountMinor: 0, netAmountMinor: 0, mismatchCount: 0 };
          row.orderCount += 1;
          row.grossAmountMinor += order.amountMinor;
          row.refundedAmountMinor += details[index]!.refunds.filter((refund) => refund.status === "processing" || refund.status === "succeeded").reduce((sum, refund) => sum + refund.amountMinor, 0);
          row.netAmountMinor = Math.max(0, row.grossAmountMinor - row.refundedAmountMinor);
          rowsMap.set(order.channel, row);
        });
        const rows = [...rowsMap.values()];
        const summary = rows.reduce((total, row) => ({ orderCount: total.orderCount + row.orderCount, grossAmountMinor: total.grossAmountMinor + row.grossAmountMinor, refundedAmountMinor: total.refundedAmountMinor + row.refundedAmountMinor, netAmountMinor: total.netAmountMinor + row.netAmountMinor, mismatchCount: 0 }), { orderCount: 0, grossAmountMinor: 0, refundedAmountMinor: 0, netAmountMinor: 0, mismatchCount: 0 });
        return paymentReconciliationBatchSchema.parse({ id: `recon_${query.dateFrom}_${query.dateTo}_${query.channel}`, dateFrom: query.dateFrom, dateTo: query.dateTo, channel: query.channel, status: "completed", generatedAt: new Date().toISOString(), summary, rows });
      }
    },
    async getPaymentStatementWorkspace(input) {
      const query = paymentReconciliationQuerySchema.parse(input);
      if (query.channel === "apple") return {
        batches: [], differences: [], jobs: [], audits: [],
        metrics: { totalDifferences: 0, resolvedDifferences: 0, overdueDifferences: 0, completionRate: 100 },
      };
      return paymentStatementWorkspaceSchema.parse(await rpc("admin_list_payment_statement_workspace", {
        p_date_from: query.dateFrom,
        p_date_to: query.dateTo,
        p_channel: query.channel,
      }));
    },
    async importPaymentStatement(input) {
      const parsed = paymentStatementImportInputSchema.parse(input);
      return paymentStatementBatchSchema.parse(await rpc("admin_import_payment_statement", { p_input: parsed }));
    },
    async rerunPaymentStatement(batchId) {
      return paymentStatementBatchSchema.parse(await rpc("admin_rerun_payment_statement", { p_batch_id: batchId }));
    },
    async triggerPaymentStatementJob(input) {
      const value = triggerPaymentStatementJobInputSchema.parse(input);
      return paymentStatementJobSchema.parse(await rpc("admin_trigger_payment_statement_job", { p_config_id: value.configId, p_statement_date: value.statementDate }));
    },
    async updatePaymentDifference(differenceId, input) {
      const value = updatePaymentDifferenceInputSchema.parse(input);
      return paymentReconciliationDifferenceSchema.parse(await rpc("admin_update_payment_reconciliation_difference", { p_difference_id: differenceId, p_input: value }));
    },
    async listPaymentWebhookEvents(input) {
      const query = paymentWebhookEventListQuerySchema.parse(input);
      return paymentWebhookEventListResultSchema.parse(await rpc("admin_list_payment_webhook_events", {
        p_keyword: query.keyword,
        p_channel: query.channel,
        p_event_type: query.eventType,
        p_status: query.status,
        p_date_from: query.dateFrom,
        p_date_to: query.dateTo,
        p_page: query.page,
        p_page_size: query.pageSize,
      }));
    },
    async getPaymentWebhookEvent(eventId) {
      return paymentWebhookEventDetailSchema.parse(await rpc("admin_get_payment_webhook_event", { p_event_id: eventId }));
    },
    async retryPaymentWebhookEvent(eventId) {
      return paymentWebhookEventSchema.parse(await rpc("admin_retry_payment_webhook_event", { p_event_id: eventId }));
    },
    createPaymentRefund(orderId, input) {
      const parsed = createPaymentRefundInputSchema.parse(input);
      return callable("createPaymentRefund", { orderId, input: parsed }, paymentRefundSchema);
    },
    async listPaymentConfigs() {
      return normalizeRows(await rpc("admin_list_payment_configs", {})).map(toPaymentConfig);
    },
    async upsertPaymentConfig(id, input) {
      const parsed = upsertPaymentConfigInputSchema.parse(input);
      return callable("upsertPaymentConfig", { ...(id ? { id } : {}), input: parsed }, paymentConfigSchema);
    },
    async deletePaymentConfig(id) {
      const result = await rpc("admin_delete_payment_config", { p_id: id });
      if (!booleanResult(result)) throw new M1ApiError("INVALID_RESPONSE", "服务响应格式无效");
    },
    async linkPaymentConfig(appId, configId) {
      const result = await rpc("admin_link_payment_config", { p_app_id: appId, p_config_id: configId });
      if (!booleanResult(result)) throw new M1ApiError("INVALID_RESPONSE", "服务响应格式无效");
    },
    async unlinkPaymentConfig(appId, configId) {
      const result = await rpc("admin_unlink_payment_config", { p_app_id: appId, p_config_id: configId });
      if (!booleanResult(result)) throw new M1ApiError("INVALID_RESPONSE", "服务响应格式无效");
    },
  };
}
